import type { ReactNode } from "react";

import { cn } from "@/lib/cn";
import { HelpTip } from "@/components/ui/HelpTip";

/** Colour carries the subject, so the same domain looks the same everywhere. */
export type CardTone = "brand" | "go" | "coral" | "ochre" | "iris" | "ink";

const MARK: Record<CardTone, string> = {
  brand: "text-brand-600",
  go: "text-go-600",
  coral: "text-coral-500",  /* decorative only: never a signal */
  ochre: "text-ochre-600",
  iris: "text-iris-500",
  ink: "text-fg-placeholder",
};

/**
 * The tinted ground, for the one panel on a screen that leads.
 *
 * One flat tint, the tone's lightest step at half strength, with the edge
 * tinted to match. It has to stay this faint: these hold paragraphs and
 * tables, and the whole point of rule 3 is that colour still means something
 * when it turns up. It was a gradient down to white once; rule 8 ended that.
 */
const GROUND: Record<CardTone, string> = {
  brand: "bg-brand-50/60 border-brand-100",
  go: "bg-go-50/60 border-go-100",
  coral: "bg-coral-50/60 border-coral-100",
  ochre: "bg-ochre-50/60 border-ochre-100",
  iris: "bg-iris-50/60 border-iris-100",
  ink: "bg-ink-50 border-line",
};

export interface CardProps {
  /** Section heading. Omit for a plain panel. */
  title?: string;
  description?: string;
  /**
   * The longer explanation, behind a question mark beside the title.
   *
   * The description is the one line that is always there; this is for the
   * paragraph a first-time reader needs and a daily user does not.
   */
  help?: ReactNode;
  /** Actions pinned to the right of the heading row. */
  actions?: ReactNode;
  /** Removes the body padding so a table can meet the panel edge. */
  flush?: boolean;
  /**
   * Shows the grip that marks a panel as something the reader could pick up
   * and rearrange. Only on panels that are actually arrangeable.
   */
  grip?: boolean;
  /** A mark before the title, colour-coded by subject. */
  icon?: ReactNode;
  tone?: CardTone;
  /**
   * Tints the panel in its tone instead of leaving it white.
   *
   * For the one panel on a screen that is the reason the screen exists. Two
   * tinted panels side by side and neither of them leads any more.
   */
  ground?: boolean;
  className?: string;
  children: ReactNode;
}

/**
 * A panel.
 *
 * The single surface the whole system is built from: white, a twelve-pixel
 * radius, drawn by a hairline border rather than a shadow — it sits in
 * the page rather than floating above it, and rule 1 keeps shadow for the
 * things that genuinely float. No rule under the heading; the space does
 * that work.
 */
export function Card({
  title,
  description,
  help,
  actions,
  flush = false,
  grip = false,
  icon,
  tone = "ink",
  ground = false,
  className,
  children,
}: CardProps) {
  const headed = Boolean(title || actions || description);

  return (
    <section
      className={cn(
        // A border, not an inset ring: a table's hovered row runs to the
        // panel's edge, and it would paint over a ring drawn inside it.
        "rounded-lg border",
        ground ? GROUND[tone] : "border-line bg-surface",
        className,
      )}
    >
      {headed ? (
        <div
          className={cn(
            "flex items-start justify-between gap-4 px-5 pt-5",
            flush ? "pb-0" : "pb-1",
          )}
        >
          <div className="flex min-w-0 items-start gap-2.5">
            {grip ? <span className="u-grip mt-1" aria-hidden="true" data-print="hide" /> : null}
            {icon ? (
              <span className={cn("mt-0.5 shrink-0", MARK[tone])} aria-hidden="true">
                {icon}
              </span>
            ) : null}
            <div className="min-w-0">
              {title ? (
                <h2 className="flex items-center gap-1.5 text-md font-semibold text-fg">
                  {title}
                  {help ? <HelpTip subject={title}>{help}</HelpTip> : null}
                </h2>
              ) : null}
              {description ? (
                <p className="mt-0.5 text-xs text-fg-subtle">{description}</p>
              ) : null}
            </div>
          </div>
          {actions ? <div className="flex shrink-0 items-center gap-2">{actions}</div> : null}
        </div>
      ) : null}
      <div className={cn(!flush && (headed ? "px-5 pb-5 pt-4" : "p-5"))}>{children}</div>
    </section>
  );
}
